import React, { useState, useEffect, useMemo } from 'react';
import {
  ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { getBonusReport } from '../api/client';
import { SiteFilter, siteParam, siteName, useSites } from '../components/SiteFilter';
import { CurrencySelector, type CurrencyMode, currencySymbol } from '../components/CurrencySelector';
import { ExportButtons } from '../components/ExportButtons';
import { formatAmount, formatNumber } from '../lib/format';
import { metricTip } from '../lib/metric-info';
import { Th } from '../components/Th';

interface BonusRow {
  date: string;
  site_id: number;
  bonus_emitted: number;
  bonus_redeemed: number;
  emitted_count: number;
  redeemed_count: number;
  unique_players: number;
}

function pct(redeemed: number, emitted: number): number {
  return emitted > 0 ? (redeemed / emitted) * 100 : 0;
}

export const BonusReport: React.FC = () => {
  const { t } = useTranslation();
  const { allSites } = useSites();
  const [data, setData] = useState<BonusRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [siteIds, setSiteIds] = useState<number[]>([]);
  const [currencyMode, setCurrencyMode] = useState<CurrencyMode>('EUR');
  const symbol = currencySymbol(currencyMode);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getBonusReport({ date_from: dateFrom, date_to: dateTo, site_ids: siteParam(allSites, siteIds), display_currency: currencyMode });
      setData(res.data || []);
    } catch (err) {
      console.error('Failed to load bonus report:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [dateFrom, dateTo, siteIds, currencyMode]);

  const totals = useMemo(() => {
    const emitted = data.reduce((a, r) => a + (r.bonus_emitted || 0), 0);
    const redeemed = data.reduce((a, r) => a + (r.bonus_redeemed || 0), 0);
    return { emitted, redeemed, rate: pct(redeemed, emitted), outstanding: emitted - redeemed };
  }, [data]);

  // Aggregate per date across sites for the trend chart.
  const chartData = useMemo(() => {
    const byDate = new Map<string, { date: string; emitted: number; redeemed: number }>();
    for (const r of data) {
      const e = byDate.get(r.date) ?? { date: r.date, emitted: 0, redeemed: 0 };
      e.emitted += r.bonus_emitted || 0;
      e.redeemed += r.bonus_redeemed || 0;
      byDate.set(r.date, e);
    }
    return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [data]);

  const cards = [
    { label: t('bonusReport.totalEmitted'), value: `${symbol}${formatAmount(totals.emitted)}` },
    { label: t('bonusReport.totalRedeemed'), value: `${symbol}${formatAmount(totals.redeemed)}` },
    { label: t('bonusReport.redemptionRate'), value: `${formatNumber(totals.rate, 1)}%` },
    { label: t('bonusReport.outstanding'), value: `${symbol}${formatAmount(totals.outstanding)}` },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{t('bonusReport.title')}</h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">{t('bonusReport.subtitle')}</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{t('filters.from')}</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{t('filters.to')}</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white" />
        </div>
        <SiteFilter selected={siteIds} onChange={setSiteIds} />
        <CurrencySelector selected={currencyMode} onChange={setCurrencyMode} />
        <ExportButtons reportType="bonus" dateFrom={dateFrom} dateTo={dateTo} siteIds={siteParam(allSites, siteIds)} />
      </div>

      {/* KPI cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
            <p className="text-sm text-slate-500 dark:text-slate-400">{c.label}</p>
            <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{c.value}</p>
          </div>
        ))}
      </div>

      {/* Trend chart: emitted vs redeemed + redemption rate line */}
      <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
        <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">{t('bonusReport.trendTitle')}</h2>
        {chartData.length === 0 ? (
          <p className="py-16 text-center text-slate-500">{t('bonusReport.noData')}</p>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9ca3af" style={{ fontSize: '12px' }} />
              <YAxis stroke="#9ca3af" style={{ fontSize: '12px' }} />
              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }}
                formatter={(v: number) => `${symbol}${formatNumber(v)}`} />
              <Legend />
              <Bar dataKey="emitted" name={t('bonusReport.emitted')} fill="#009EA0" radius={[4, 4, 0, 0]} />
              <Bar dataKey="redeemed" name={t('bonusReport.redeemed')} fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              {[
                { l: t('bonusReport.columnDate'), i: 'date' },
                { l: t('bonusReport.columnSite'), i: 'site' },
                { l: t('bonusReport.emitted'), i: 'bonusEmitted' },
                { l: t('bonusReport.redeemed'), i: 'bonusRedeemed' },
                { l: t('bonusReport.redemptionRate'), i: 'redemptionRate' },
                { l: t('bonusReport.columnEmittedCount'), i: 'emittedCount' },
                { l: t('bonusReport.columnRedeemedCount'), i: 'redeemedCount' },
                { l: t('bonusReport.columnUniquePlayers'), i: 'uniquePlayers' },
              ].map((h) => (
                <Th key={h.l} tip={metricTip(h.i)}>{h.l}</Th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {loading && data.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">{t('filters.loading')}</td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">{t('bonusReport.noData')}</td></tr>
            ) : data.map((row, i) => (
              <tr key={i} className="bg-white dark:bg-slate-900">
                <td className="px-4 py-3 text-slate-900 dark:text-white">{row.date}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white" title={`ID ${row.site_id}`}>{siteName(allSites, row.site_id)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{symbol}{formatAmount(row.bonus_emitted || 0)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{symbol}{formatAmount(row.bonus_redeemed || 0)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{formatNumber(pct(row.bonus_redeemed, row.bonus_emitted), 1)}%</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{formatNumber(row.emitted_count || 0)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{formatNumber(row.redeemed_count || 0)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{formatNumber(row.unique_players || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
